import React, { useState, useMemo, useEffect } from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, Button, Grid, makeStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import GetAppIcon from '@material-ui/icons/GetApp';
import { countOrACVFormatter } from '../../../util/customFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { clearRemoveQuotaReps, selectRemoveQuotaSuccess } from '../redux/management.slice';

const useDataGridStyles = makeStyles(() => ({
  dataGridContainer: {
    height: 'calc(100% - 48px)',
    width: '100%',
    overflow: 'hidden',
    '& .MuiDataGrid-root': {
      border: '1px solid rgba(224, 224, 224, 1)',
      width: '100%',
    },
    '& .MuiDataGrid-window': {
      left: 0,
      right: 0,
      bottom: 0,
      position: 'absolute',
      overflowX: 'hidden',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#4472c4 !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#4472c4 !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-iconSeparator': {
      color: 'white !important',
    },
    '& .MuiDataGrid-sortIcon': {
      color: 'white !important',
    },
    '& .MuiDataGrid-menuIcon': {
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      color: 'white !important',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#e3ecfa !important',
      },
    },
  },
  tableWrapper: {
    height: '55vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const RepTable = ({
  filteredReps, team, fiscalQuarter, teamOptions, fiscalQuarterOptions, search, onSearchChange, onRowClick, onBack, classes, isTeamMode = false, isCompanyMode = false
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const dispatch = useDispatch();
  const dataGridClasses = useDataGridStyles();
  const currencySymbol = useSelector(state => state.app.user.Currency);
  const removeQuotaSuccess = useSelector(selectRemoveQuotaSuccess);

  // Custom component for no rows overlay
  const NoRowsOverlay = () => {
    const entityType = isTeamMode ? 'Teams' : isCompanyMode ? 'Company' : 'Reps';
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <Typography variant="body1" color="textSecondary">
          No {entityType} Quota Data found for the selected filters.
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    return () => {
      dispatch(clearRemoveQuotaReps());
    };
  }, []);

  useEffect(() => {
    if (filteredReps && filteredReps.length > 0 && !isLoaded) {
      dispatch({ type: "info_message_generic", payload: { type: "success", message: "Quota Reps Loaded" } });
      setIsLoaded(true);
    }
  }, [filteredReps]);

  // Define columns based on mode
  const columns = useMemo(() => {
    if (isTeamMode) {
      return [
        {
          field: 'team',
          headerName: 'Team',
          flex: 1.5,
          minWidth: 200,
          sortable: true,
          disableColumnMenu: true,
        },
       
        {
          field: 'quota',
          headerName: 'Quota',
          flex: 1,
          minWidth: 150,
          sortable: true,
          disableColumnMenu: true,
          align: 'right',
          headerAlign: 'right',
          valueFormatter: (params) => countOrACVFormatter(params.value, currencySymbol),
        },
         {
          field: 'nqpGoal',
          headerName: 'NQP Goal',
          flex: 1,
          minWidth: 150,
          sortable: true,
          disableColumnMenu: true,
          align: 'right',
          headerAlign: 'right',
          valueFormatter: (params) => countOrACVFormatter(params.value, currencySymbol),
        },
      ];
    } else if (isCompanyMode) {
      return [
        {
          field: 'fiscalQuarter',
          headerName: 'Fiscal Quarter',
          flex: 1,
          minWidth: 150,
          sortable: true,
          disableColumnMenu: true,
        },
         {
          field: 'quota',
          headerName: 'Quota',
          flex: 1,
          minWidth: 150,
          sortable: true,
          disableColumnMenu: true,
          align: 'right',
          headerAlign: 'right',
          valueFormatter: (params) => countOrACVFormatter(params.value, currencySymbol),
        },
        {
          field: 'nqpGoal',
          headerName: 'NQP Goal',
          flex: 1,
          minWidth: 150,
          sortable: true,
          disableColumnMenu: true,
          align: 'right',
          headerAlign: 'right',
          valueFormatter: (params) => countOrACVFormatter(params.value, currencySymbol),
        }
       
      ];
    } else {
      return [
        {
          field: 'fullName',
          headerName: 'Rep',
          flex: 1.5,
          minWidth: 200,
          sortable: true,
          disableColumnMenu: true,
        },
        {
          field: 'email',
          headerName: 'Email',
          flex: 2,
          minWidth: 250,
          sortable: true,
          disableColumnMenu: true,
        },
      ];
    }
  }, [isTeamMode, isCompanyMode, currencySymbol]);

  // Transform filteredReps into rows for DataGrid
  const rows = useMemo(() => {
    return filteredReps.map((rep, idx) => ({
      id: idx,
      team: rep.team || '-',
      nqpGoal: rep.nqpGoal,
      quota: rep.quota,
      fiscalQuarter: rep.fiscalQuarter || '-',
      fullName: rep.fullName || '-',
      email: rep.email || '-',
      _original: rep, // Keep original data for row click
    }));
  }, [filteredReps]);

  // CSV Export Function
  const handleExportCSV = () => {
    // Build CSV content
    const headers = columns.map(col => col.headerName).join(',');
    const csvRows = rows.map(row => {
      return columns.map(col => {
        const field = col.field;
        let value = row[field];

        // Apply formatter if exists
        if (col.valueFormatter && value !== undefined && value !== null) {
          value = col.valueFormatter({ value });
        }

        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }

        return value;
      }).join(',');
    });

    const csvContent = [headers, ...csvRows].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${isTeamMode ? 'team' : isCompanyMode ? 'company' : 'rep'}_quota_data.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Custom renderValue function for Team Select
  const renderTeamValue = (selected) => {
    console.log("NEW_TEAM_SELECT", selected);
    
    // Handle null, undefined, or empty string
    if (!selected) {
      return '';
    }
    
    // If selected is not an array (single select mode)
    if (!Array.isArray(selected)) {
      // Handle empty string
      if (selected === '') {
        return '';
      }
      
      const teamObj = teamOptions?.find(t => (t.id || t.teamId || t) === selected);
      return teamObj ? (teamObj.name || teamObj.teamName || selected) : selected;
    }
    
    // Handle empty array
    if (selected.length === 0) {
      return '';
    }
    
    // Handle array cases (multiple select mode)
    if (selected.includes('All') || selected.length === (teamOptions?.length || 0)) {
      return 'All Teams';
    }
    
    if (selected.length >= 3) {
      return `${selected.length} teams selected`;
    }
    
    // If less than 3, show actual team names
    return selected.map(teamId => {
      const teamObj = teamOptions?.find(t => (t.id || t.teamId || t) === teamId);
      return teamObj ? (teamObj.name || teamObj.teamName || teamId) : teamId;
    }).join(', ');
  };

  return (
    <>
      <Box className={classes.filterRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <Box style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          {!isCompanyMode && (
            <Button
              color="primary"
              onClick={onBack}
              style={{ minWidth: 0, padding: 6 }}
            >
              <ArrowBackIcon style={{ fontSize: 24, color: '#4472c4' }} />
            </Button>
          )}
          <TextField
            className={classes.searchBox}
            variant="outlined"
            size="small"
            placeholder={isTeamMode ? "Search Teams..." : isCompanyMode ? "Search Fiscal Quarter..." : "Search Reps..."}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            style={{ minWidth: 300 }}
          />
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {!isTeamMode && !isCompanyMode && (
            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <InputLabel>Team</InputLabel>
              <Select
                value={team || ''}
                label="Team"
                disabled
                renderValue={renderTeamValue}
              >
                <MenuItem key="All" value="All">All</MenuItem>
                {Array.isArray(teamOptions) && teamOptions.map((t) => (
                  <MenuItem key={t.id || t.teamId || t} value={t.id || t.teamId || t}>
                    {t.name || t.teamName || t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!isCompanyMode && (
            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <InputLabel>Fiscal Quarter</InputLabel>
              <Select value={fiscalQuarter || ''} label="Fiscal Quarter" disabled>
                {Array.isArray(fiscalQuarterOptions) && fiscalQuarterOptions.map((fq) => (
                  <MenuItem key={fq.id || fq.fiscal_quarter || fq} value={fq.id || fq.fiscal_quarter || fq}>
                    {fq.name || fq.fiscal_quarter || fq}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      <Grid container>
        <Grid item xs={12}>
          <div className={dataGridClasses.tableWrapper}>
            <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
              <Tooltip title="Download as CSV" arrow>
                <IconButton
                  onClick={handleExportCSV}
                  size="small"
                  style={{ padding: '4px' }}
                >
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <div className={dataGridClasses.dataGridContainer}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={rows.length === 0 && removeQuotaSuccess === null}
                onRowClick={(params) => onRowClick(params.row._original)}
                disableSelectionOnClick
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 25, 50, 100]}
                pagination
                disableColumnMenu
                components={{
                  NoRowsOverlay: NoRowsOverlay,
                }}
              />
            </div>
          </div>
        </Grid>
      </Grid>
    </>
  );
};

export default RepTable;